/**
 * Tag Editor Component
 * Rich-text editor with tag insertion powered by HugeRTE.
 * Implements ControlValueAccessor and Validator for Angular Forms integration.
 */

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  forwardRef,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { ReplaySubject, switchMap } from 'rxjs'
import {
  AbstractControl,
  ControlValueAccessor,
  FormsModule,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
} from '@angular/forms'
import { DialogModule } from 'primeng/dialog'
import { TreeModule, TreeNodeSelectEvent } from 'primeng/tree'
import { Button } from 'primeng/button'
import { Tooltip } from 'primeng/tooltip'
import { TranslocoModule, TranslocoService } from '@jsverse/transloco'
import { EditorModule } from '@hugerte/hugerte-angular'
import { Editor, RawEditorOptions } from 'hugerte'
import { createClassApplier } from '@notjosh/rangy-classapplier'
import { TagManagerService } from './services/tag-manager.service'
import { TagEditorService } from './services/tag-editor.service'
import { TagEditorTreeData, TagEditorTreeNode } from './tag-editor-tree-node'
import { EditorContentChangeEvent, InsertedTagCustom } from './tag-editor.model'
import {
  CSS_CLASSES,
  DOM_ELEMENT_NAMES,
  HTML_ATTRIBUTES,
  HTML_VALUES,
  TAG_EDITOR_STRINGS,
  TAG_EDITOR_THRESHOLDS,
} from './tag-editor.constants'
import { generateColorFromName, generateWCAGCompliantTagColors } from '../../../../shared/helpers/color.utils'
import { createTagEditorConfig } from './tag-editor.config'

@Component({
  selector: 'app-tag-editor',
  templateUrl: './tag-editor.component.html',
  styleUrls: ['./tag-editor.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, TranslocoModule, EditorModule, DialogModule, TreeModule, Button, Tooltip],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TagEditorComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => TagEditorComponent),
      multi: true,
    },
  ],
})
export class TagEditorComponent implements OnInit, OnChanges, OnDestroy, ControlValueAccessor, Validator {
  private readonly cdr = inject(ChangeDetectorRef)
  private readonly tagManagerService = inject(TagManagerService)
  private readonly translocoService = inject(TranslocoService)
  private readonly tagEditorService = inject(TagEditorService)
  private readonly destroyRef = inject(DestroyRef)
  private readonly clientIdChanges$ = new ReplaySubject<number>(1)

  @Input() treeNodes: ReadonlyArray<TagEditorTreeNode> = []
  @Input() placeholder = ''
  @Input() clientId: number | null = null
  @Input() requireTags = false

  @Output() valueChange = new EventEmitter<string>()
  @Output() tagDataChange = new EventEmitter<EditorContentChangeEvent>()

  protected insertedTags: InsertedTagCustom[] = []
  protected cursorTagId: string | null = null
  protected hoveredTagId: string | null = null
  protected editorInstance: Editor | null = null
  protected editorConfig!: RawEditorOptions
  protected showTagSelector = false
  protected selectedTreeNode: TagEditorTreeNode | null = null
  protected internalTreeNodes: TagEditorTreeNode[] = []
  protected useInternalLoading = false

  private pendingContent: string | null = null
  private pendingDisabled: boolean | null = null
  private savedBookmark: ReturnType<Editor['selection']['getBookmark']> | null = null
  private ionModal: Element | null = null
  private lastProcessedContent = ''
  private syncTagsTimer: ReturnType<typeof setTimeout> | null = null
  private scrollTimer1: ReturnType<typeof setTimeout> | null = null
  private scrollTimer2: ReturnType<typeof setTimeout> | null = null

  protected get displayTreeNodes(): ReadonlyArray<TagEditorTreeNode> {
    return this.treeNodes.length > 0 ? this.treeNodes : this.internalTreeNodes
  }

  protected get dialogAppendTarget(): HTMLElement | string {
    return (this.ionModal as HTMLElement | null) ?? 'body'
  }

  protected get ipJournalUrl(): string | null {
    if (!this.clientId) return null
    return `${TAG_EDITOR_STRINGS.IP_JOURNAL_URL_PREFIX}${this.clientId}${TAG_EDITOR_STRINGS.IP_JOURNAL_URL_SUFFIX}`
  }

  // ========== Lifecycle ==========

  ngOnInit(): void {
    this.editorConfig = createTagEditorConfig({
      placeholder: this.placeholder,
      transloco: this.translocoService,
      pastePreprocess: (_plugin, args) => {
        args.content = this.tagManagerService.migrateOldContent(args.content)
        args.content = this.tagManagerService.assignUniqueIdsToTagsInHTML(args.content)
        args.content = args.content.replace(/\s*id="docs-internal-guid-[^"]*"/g, '')
      },
      setup: (editor) => {
        editor.ui.registry.addButton('inserttag', {
          text: this.translocoService.translate('TAG_EDITOR.INSERT_TAG'),
          icon: 'plus',
          tooltip: this.translocoService.translate('TAG_EDITOR.INSERT_TAG_TOOLTIP'),
          onAction: () => {
            this.savedBookmark = editor.selection.getBookmark(2)
            this.selectedTreeNode = null
            this.showTagSelector = true
            this.cdr.markForCheck()
          },
        })

        // Move TinyMCE's silver-sink inside the nearest ion-modal so Ionic's
        // focus trap doesn't intercept focus when TinyMCE dialogs (link, etc.) open.
        editor.on('OpenWindow', () => {
          const sink = document.querySelector('.tox-silver-sink') as HTMLElement | null
          if (sink && this.ionModal && !this.ionModal.contains(sink)) {
            this.ionModal.appendChild(sink)
          }
        })

        editor.on('mouseover', (evt: MouseEvent) => {
          const tagEl = (evt.target as HTMLElement).closest<HTMLElement>('[data-tag-id]')
          if (!tagEl) return
          const id = tagEl.dataset['tagId']!
          this.hoveredTagId = id
          this.highlightEditorTag(id)
          this.cdr.markForCheck()
        })

        editor.on('mouseout', (evt: MouseEvent) => {
          const tagEl = (evt.target as HTMLElement).closest<HTMLElement>('[data-tag-id]')
          if (!tagEl) return
          const relatedTarget = evt.relatedTarget as HTMLElement | null
          if (relatedTarget && tagEl.contains(relatedTarget)) return
          this.hoveredTagId = null
          this.highlightEditorTag(tagEl.dataset['tagId']!)
          this.cdr.markForCheck()
        })

        editor.on('NodeChange', () => {
          const node = editor.selection.getStart()
          const tagEl = node?.closest?.('[data-tag-id]') ?? null
          const id = (tagEl as HTMLElement | null)?.dataset['tagId'] ?? null
          if (id === this.cursorTagId) return
          const prev = this.cursorTagId
          this.cursorTagId = id
          if (prev) this.highlightEditorTag(prev)
          if (id) this.highlightEditorTag(id)
          this.cdr.markForCheck()
        })
      },
    })

    this.clientIdChanges$
      .pipe(
        switchMap((clientId) => this.tagEditorService.getTreeForClient(clientId)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((nodes) => {
        this.internalTreeNodes = nodes
        this.cdr.markForCheck()
      })
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['clientId'] && this.clientId != null && this.treeNodes.length === 0) {
      this.useInternalLoading = true
      this.clientIdChanges$.next(this.clientId)
    }
  }

  ngOnDestroy(): void {
    if (this.syncTagsTimer !== null) clearTimeout(this.syncTagsTimer)
    if (this.scrollTimer1 !== null) clearTimeout(this.scrollTimer1)
    if (this.scrollTimer2 !== null) clearTimeout(this.scrollTimer2)
  }

  // ========== ControlValueAccessor ==========

  protected onChange: (value: string) => void = () => {}
  protected onTouched: () => void = () => {}

  writeValue(value: string): void {
    const content = this.tagManagerService.migrateOldContent(value ?? '')
    if (this.editorInstance) {
      this.editorInstance.setContent(content)
      if (this.syncTagsTimer !== null) clearTimeout(this.syncTagsTimer)
      this.syncTagsTimer = setTimeout(() => this.syncInsertedTags())
    } else {
      this.pendingContent = content
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn
  }

  setDisabledState(isDisabled: boolean): void {
    if (this.editorInstance) {
      this.editorInstance.mode.set(isDisabled ? 'readonly' : 'design')
    } else {
      this.pendingDisabled = isDisabled
    }
  }

  // ========== Validator ==========

  validate(control: AbstractControl): ValidationErrors | null {
    if (!this.requireTags) return null
    const html: string = (control.value as string) || ''
    if (!html.trim() || this.insertedTags.length < TAG_EDITOR_THRESHOLDS.MIN_TAGS_COUNT) {
      return { tagRequired: true }
    }
    return null
  }

  // ========== Editor event handlers ==========

  protected onEditorInit(event: { event: Event; editor: Editor }): void {
    this.editorInstance = event.editor
    this.ionModal = event.editor.getContainer()?.closest('ion-modal') ?? null

    if (this.pendingContent !== null) {
      this.editorInstance.setContent(this.pendingContent)
      this.pendingContent = null
    }

    if (this.pendingDisabled !== null) {
      this.editorInstance.mode.set(this.pendingDisabled ? 'readonly' : 'design')
      this.pendingDisabled = null
    }

    if (this.syncTagsTimer !== null) clearTimeout(this.syncTagsTimer)
    this.syncTagsTimer = setTimeout(() => this.syncInsertedTags())
  }

  protected onEditorChange(event: { event: Event; editor: Editor }): void {
    const content = event.editor.getContent()
    if (content === this.lastProcessedContent) return
    this.lastProcessedContent = content
    this.insertedTags = [...this.tagManagerService.parseTagsFromContent(content)]
    this.valueChange.emit(content)
    this.tagDataChange.emit({ content, tags: this.insertedTags })
    this.onChange(content)
    this.cdr.markForCheck()
  }

  // ========== Sidebar ==========

  protected onSidebarMouseenter(id: string): void {
    this.hoveredTagId = id
    this.highlightEditorTag(id)
  }

  protected onSidebarMouseleave(id: string): void {
    this.hoveredTagId = null
    this.highlightEditorTag(id)
  }

  private highlightEditorTag(id: string): void {
    if (!this.editorInstance) return
    const active = this.hoveredTagId === id || this.cursorTagId === id
    this.editorInstance.getBody().querySelectorAll<HTMLElement>(`[data-tag-id="${id}"]`).forEach((el) => {
      el.classList.toggle('hovered', active)
    })
  }

  protected scrollToTag(id: string): void {
    if (!this.editorInstance) return
    const tagEl = this.editorInstance.getBody().querySelector<HTMLElement>(`[data-tag-id="${id}"]`)
    if (!tagEl) return
    tagEl.scrollIntoView({ behavior: 'smooth', block: 'center' })
    tagEl.classList.add('tag-highlight')

    if (this.scrollTimer1 !== null) clearTimeout(this.scrollTimer1)
    this.scrollTimer1 = setTimeout(() => {
      tagEl.classList.remove('tag-highlight')
      tagEl.classList.add('tag-highlight-fade')
      if (this.scrollTimer2 !== null) clearTimeout(this.scrollTimer2)
      this.scrollTimer2 = setTimeout(() => tagEl.classList.remove('tag-highlight-fade'), 400)
    }, 1200)
  }

  // ========== Tag selector ==========

  protected closeTagSelector(): void {
    this.showTagSelector = false
    this.savedBookmark = null
  }

  protected onTreeNodeSelect(event: TreeNodeSelectEvent): void {
    if (!this.editorInstance) return
    const editor = this.editorInstance

    const label = event.node?.label
    const data = event.node?.data as TagEditorTreeData | undefined
    if (!label || !data) return
    const bookmark = this.savedBookmark
    this.closeTagSelector()
    if (bookmark) editor.selection.moveToBookmark(bookmark)

    if (editor.selection.isCollapsed()) return

    const id = this.tagManagerService.generateTagId()
    const bgColor = generateColorFromName(label)
    const rgb = this.tagManagerService.hexToRgb(bgColor)

    const attrs: Record<string, string> = {
      [HTML_ATTRIBUTES.DATA_TAG_ID]: id,
      [HTML_ATTRIBUTES.ID]: id,
      [HTML_ATTRIBUTES.DATA_LABEL]: label,
      [HTML_ATTRIBUTES.DATA_COLOR]: bgColor,
      [HTML_ATTRIBUTES.TITLE]: `${HTML_VALUES.TAG_TITLE_PREFIX}${label}`,
    }
    if (data.categoryName) attrs[HTML_ATTRIBUTES.DATA_CATEGORY] = data.categoryName
    if (data.categoryId !== undefined) attrs[HTML_ATTRIBUTES.DATA_CATEGORY_ID] = data.categoryId.toString()

    const applier = createClassApplier(CSS_CLASSES.TAG_ANCHOR, {
      elementTagName: DOM_ELEMENT_NAMES.SPAN,
      elementAttributes: attrs,
      useExistingElements: false,
      onElementCreate: (el: HTMLElement) => {
        if (rgb) el.style.setProperty('--tag-color', `${rgb.r}, ${rgb.g}, ${rgb.b}`)
      },
    })
    applier.applyToSelection(editor.getWin())
    editor.fire('change')
  }

  protected openIpJournalInNewTab(): void {
    if (this.ipJournalUrl) window.open(this.ipJournalUrl, '_blank', 'noopener,noreferrer')
  }

  // ========== Public API ==========

  public removeTag(id: string): void {
    if (!this.editorInstance) return
    const tagEls = this.editorInstance.getBody().querySelectorAll<HTMLElement>(`[data-tag-id="${id}"]`)
    if (tagEls.length === 0) return
    tagEls.forEach((el) => el.replaceWith(...Array.from(el.childNodes)))
    this.editorInstance.fire('change')
  }

  public refreshTreeNodes(): void {
    if (this.clientId != null) {
      this.clientIdChanges$.next(this.clientId)
    }
  }

  protected onRemoveTagClick(id: string, event: MouseEvent): void {
    event.stopPropagation()
    this.removeTag(id)
  }

  // ========== Private helpers ==========

  private syncInsertedTags(): void {
    if (!this.editorInstance) return
    const content = this.editorInstance.getContent()
    this.insertedTags = [...this.tagManagerService.parseTagsFromContent(content)]
    this.cdr.markForCheck()
  }
}
